<template>
  <div v-if="visible" class="music-player">
    <div class="header">
      <div class="title" ref="titleViewport">
        <div
          class="title-track"
          ref="titleTrack"
          :class="{ 'is-marquee': marquee.active }"
          :style="marqueeStyle"
        >
          <span class="title-text" :title="displayTitle">{{ displayTitle }}</span>
        </div>
      </div>
      <Button size="small" type="text" class="close-btn" @click="handleClose">×</Button>
    </div>

    <div class="controls">
      <Button size="small" type="primary" @click="togglePlay" :disabled="!src">
        {{ isPlaying ? '暂停' : '播放' }}
      </Button>
      <Button size="small" class="ml8" @click="stop" :disabled="!src">停止</Button>

      <div class="time ml12">
        <span class="time-text">{{ formatTime(currentTime) }}</span>
        <span class="time-sep">/</span>
        <span class="time-text">{{ formatTime(duration) }}</span>
      </div>
    </div>

    <div class="progress">
      <input
        class="range"
        type="range"
        :min="0"
        :max="duration || 0"
        :step="0.2"
        :value="seeking ? seekTime : currentTime"
        :disabled="!src || !duration"
        @input="onSeekInput"
        @change="onSeekChange"
        @mousedown="seeking = true"
        @mouseup="seeking = false"
        @touchstart="seeking = true"
        @touchend="seeking = false"
      />
    </div>

    <audio
      ref="audio"
      :src="src"
      :preload="preload"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnded"
      @error="onError"
    ></audio>
  </div>
</template>

<script>
export default {
  name: "MusicPlayer",
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    src: {
      type: String,
      default: "",
    },
    musicName: {
      type: String,
      default: "",
    },
    singerName: {
      type: String,
      default: "",
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    preload: {
      type: String,
      default: "auto",
    },
  },
  data() {
    return {
      visible: this.value,
      isPlaying: false,
      duration: 0,
      currentTime: 0,
      seeking: false,
      seekTime: 0,
      marquee: {
        active: false,
        distance: 0,
        duration: 0,
      },
    };
  },
  computed: {
    displayTitle() {
      const name = String(this.musicName || "").trim();
      const singer = String(this.singerName || "").trim();
      if (name && singer) return `${name} - ${singer}`;
      return name || singer || "";
    },
    marqueeStyle() {
      if (!this.marquee.active) return {};
      return {
        "--marquee-distance": `${this.marquee.distance}px`,
        "--marquee-duration": `${this.marquee.duration}s`,
      };
    },
  },
  watch: {
    value(val) {
      this.visible = val;
      if (!val) {
        this.pause();
      }
    },
    visible(val) {
      this.$emit("input", val);
      if (!val) {
        this.pause();
      }
    },
    musicName() {
      this.$nextTick(this.updateMarquee);
    },
    singerName() {
      this.$nextTick(this.updateMarquee);
    },
    src() {
      this.resetAudioState();
      this.$nextTick(() => {
        if (this.autoplay && this.visible && this.src) {
          this.play();
        }
      });
    },
  },
  mounted() {
    if (this.autoplay && this.visible && this.src) {
      this.play();
    }
    this.updateMarquee();
    window.addEventListener("resize", this.updateMarquee, { passive: true });
  },
  beforeDestroy() {
    this.isPlaying = false
    this.pause();
    window.removeEventListener("resize", this.updateMarquee);
  },
  methods: {
    getAudioEl() {
      return this.$refs.audio;
    },
    handleClose() {
      this.visible = false;
      this.$emit("close");
    },
    resetAudioState() {
      this.isPlaying = false;
      this.duration = 0;
      this.currentTime = 0;
      this.seekTime = 0;
      this.seeking = false;
      const audio = this.getAudioEl();
      if (audio) {
        audio.currentTime = 0;
      }
    },
    play() {
      const audio = this.getAudioEl();
      if (!audio) return;
      const p = audio.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {});
      }
    },
    pause() {
      const audio = this.getAudioEl();
      if (!audio) return;
      audio.pause();
    },
    togglePlay() {
      const audio = this.getAudioEl();
      if (!audio) return;
      if (audio.paused) this.play();
      else this.pause();
    },
    stop() {
      const audio = this.getAudioEl();
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
    },
    onLoadedMetadata() {
      const audio = this.getAudioEl();
      if (!audio) return;
      this.duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    },
    onTimeUpdate() {
      const audio = this.getAudioEl();
      if (!audio) return;
      if (!this.seeking) {
        this.currentTime = audio.currentTime || 0;
      }
      if (!this.duration && Number.isFinite(audio.duration)) {
        this.duration = audio.duration;
      }
    },
    onPlay() {
      this.isPlaying = true;
    },
    onPause() {
      this.isPlaying = false;
    },
    onEnded() {
      this.isPlaying = false;
      this.currentTime = this.duration || 0;
    },
    onError() {
      this.isPlaying = false;
      this.$emit("error");
    },
    onSeekInput(e) {
      const value = Number(e && e.target ? e.target.value : 0);
      this.seekTime = Number.isFinite(value) ? value : 0;
    },
    onSeekChange() {
      const audio = this.getAudioEl();
      if (!audio) return;
      audio.currentTime = this.seekTime;
      this.currentTime = this.seekTime;
    },
    formatTime(seconds) {
      const s = Math.max(0, Math.floor(Number(seconds) || 0));
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
    },
    updateMarquee() {
      const viewport = this.$refs.titleViewport;
      const track = this.$refs.titleTrack;
      if (!viewport || !track) return;

      const viewportWidth = viewport.clientWidth || 0;
      const trackWidth = track.scrollWidth || 0;
      const distance = Math.max(0, trackWidth - viewportWidth);
      const active = distance > 6;

      this.marquee.active = active;
      this.marquee.distance = distance;
      if (active) {
        const seconds = Math.min(18, Math.max(6, distance / 28));
        this.marquee.duration = Number(seconds.toFixed(2));
      } else {
        this.marquee.duration = 0;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.music-player {
  position: fixed;
  left: 50%;
  top: 30px;
  transform: translateX(-50%);
  z-index: 9999;
  width: 300px;
  max-width: calc(100vw - 24px);
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  padding: 12px 12px 10px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #17233d;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
}

.title-track {
  display: inline-block;
  max-width: 100%;
  transform: translateX(0);
}

.title-track.is-marquee {
  animation: marquee-x var(--marquee-duration) linear infinite alternate;
}

.title-text {
  display: inline-block;
  font-weight: 600;
}

.sep {
  opacity: 0.6;
}

@keyframes marquee-x {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-1 * var(--marquee-distance)));
  }
}

.close-btn {
  padding: 0 6px;
  font-size: 18px;
  line-height: 18px;
  height: 22px;
}

.controls {
  display: flex;
  align-items: center;
  margin-top: 10px;
}

.ml8 {
  margin-left: 8px;
}

.ml12 {
  margin-left: 12px;
}

.time {
  margin-left: auto;
  font-size: 12px;
  color: #515a6e;
  display: flex;
  align-items: center;
  gap: 6px;
}

.progress {
  margin-top: 10px;
}

.range {
  width: 100%;
  height: 4px;
  appearance: none;
  background: #e8eaec;
  border-radius: 999px;
  outline: none;
}

.range::-webkit-slider-thumb {
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #2d8cf0;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}

.range:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/musicPlayer/MusicPlayer.vue